import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Phone } from "lucide-react";
import { BRAND } from "../data/site";

export default function WhatsAppFAB() {
  const [open, setOpen] = useState(false);
  const [pulsed, setPulsed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPulsed(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const buildWa = (raw) =>
    `https://wa.me/${raw}?text=${encodeURIComponent(
      "Hi Happy Journey 4U! I'd like to plan a trip."
    )}`;

  return (
    <div
      data-testid="whatsapp-fab"
      className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[60] flex flex-col items-end gap-3"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="glass rounded-2xl p-5 w-[280px] shadow-2xl"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                  Chat with us
                </div>
                <div className="font-playfair text-lg font-bold text-slate-900 mt-1 leading-tight">
                  Pick a number to WhatsApp
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="whatsapp-fab-close"
                className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-slate-700" />
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              {BRAND.phones.map((p, i) => (
                <a
                  key={p.number}
                  href={buildWa(p.raw)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`fab-wa-${i}`}
                  className="group flex items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-[#25D366] hover:text-white border border-white/60 transition-all"
                >
                  <div className="h-10 w-10 rounded-xl bg-[#25D366]/15 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                    <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[0.7rem] uppercase tracking-[0.18em] text-slate-500 group-hover:text-white/80">
                      {p.label}
                    </div>
                    <div className="font-semibold text-slate-900 group-hover:text-white">
                      {p.number}
                    </div>
                  </div>
                </a>
              ))}

              <a
                href={`tel:${BRAND.phones[0].number.replace(/\s/g, "")}`}
                data-testid="fab-call"
                className="flex items-center gap-2 justify-center mt-1 p-3 rounded-xl text-sm font-medium text-brand-ocean hover:bg-white/60 border border-white/40 transition-colors"
              >
                <Phone className="w-4 h-4" /> Or call directly
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="whatsapp-fab-btn"
        aria-label="Open WhatsApp chat"
        whileTap={{ scale: 0.92 }}
        className="relative h-16 w-16 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-[0_12px_40px_rgba(37,211,102,0.55)] hover:shadow-[0_18px_50px_rgba(37,211,102,0.7)] hover:scale-110 transition-all"
      >
        <MessageCircle className="w-7 h-7" />
        {pulsed && !open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40" />
        )}
        {!open && (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-brand-orange ring-2 ring-white animate-pulse" />
        )}
      </motion.button>
    </div>
  );
}
